import React, { useEffect, useRef, useState } from 'react';
import {
  ImageSourcePropType,
  LayoutChangeEvent,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import Pill from 'shared/components/vxl/navigation/Pill/Pill';
import theme from 'shared/styles/theme';

export interface PillConfigProps {
  iconKey?: string;
  imageSource?: ImageSourcePropType;
  iconColor?: string;
  id: string;
  labelText: string;
  testID?: string;
}

export interface AutoScrollPillGroupProps {
  pillConfigs: PillConfigProps[];
  onSelection: (config: PillConfigProps) => void;
  selectedPillId?: string;
}

const AutoScrollPillGroup = ({
  pillConfigs,
  onSelection,
  selectedPillId,
}: AutoScrollPillGroupProps) => {
  const scrollViewRef = useRef<ScrollView>(null);
  const [parentWidth, setParentWidth] = useState(0);
  const [pillWidths, setPillWidths] = useState<{ [key: string]: number }>({});

  const PILL_GAP = theme.spacing.s;

  const handleLayout = (event: LayoutChangeEvent): void => {
    setParentWidth(event.nativeEvent.layout.width);
  };

  const handlePillLayout =
    (pillId: string) =>
    (event: LayoutChangeEvent): void => {
      const { width } = event.nativeEvent.layout;
      setPillWidths((prev) => ({ ...prev, [pillId]: width }));
    };

  const onPillPress = (config: PillConfigProps): void => {
    onSelection(config);
  };

  useEffect(() => {
    const calculateScrollOffset = (): number => {
      const selectedIndex = pillConfigs.findIndex(
        (p) => p.id === selectedPillId,
      );
      if (selectedIndex === -1) {
        return 0;
      }

      let offset = 0;
      for (let i = 0; i < selectedIndex; i++) {
        const pillWidth = pillWidths[pillConfigs[i].id] || 0;
        offset += pillWidth + PILL_GAP;
      }

      return offset;
    };

    const offset = calculateScrollOffset();

    if (
      offset > 0 &&
      scrollViewRef.current &&
      parentWidth > 0 &&
      selectedPillId &&
      Object.keys(pillWidths).length > 0
    ) {
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({
          x: offset,
          animated: true,
        });
      }, 200);
    }
  }, [selectedPillId, parentWidth, pillWidths, pillConfigs, PILL_GAP]);

  return (
    <View style={styles.wrapper} onLayout={handleLayout}>
      <ScrollView
        ref={scrollViewRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollViewContent}
      >
        <View style={styles.container}>
          {pillConfigs.map((config) => (
            <View key={config.id} onLayout={handlePillLayout(config.id)}>
              <Pill
                isSelected={selectedPillId === config.id}
                labelText={config.labelText}
                iconKey={config.iconKey}
                imageSource={config.imageSource}
                iconColor={config.iconColor}
                onPress={() => onPillPress(config)}
                testID={config.testID}
              />
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: theme.spacing.s,
    paddingVertical: theme.spacing.s,
    width: '100%',
  },
  scrollViewContent: {
    backgroundColor: theme.color.background.primary,
    flexGrow: 1,
    paddingHorizontal: theme.spacing.m,
  },
  wrapper: {
    width: '100%',
  },
});

export default AutoScrollPillGroup;
